import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  InputGroup,
  Spinner,
  Alert,
} from "react-bootstrap";
import {
  ArrowLeft,
  BoxArrowRight,
  Bell,
  PersonCircle,
  CalendarEvent,
  Search,
  People,
  Book,
  Check2Circle,
  Layers,
  Robot,
} from "react-bootstrap-icons";
import SettingsGear from "../components/SettingsGear";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../App.css";

import LOGO_URL from "../img/tmis_logo_orig.png";

const API_BASE = process.env.REACT_APP_API || "";

const ENDPOINT_VERIFIED = `${API_BASE}/getActivityByVerifiedUser`;
const ENDPOINT_GET_SETTINGS = `${API_BASE}/getAllSetting`;
const ENDPOINT_UPDATE_SETTING = `${API_BASE}/updateSetting`;

const NAV_BLUE = "#183B72";
const PANEL_BG = "#F1F3F6";
const PANEL_SURFACE = "#E7EAEE";
const BRAND = "#3D74F5";

const BASE_YEAR = 2025;
const CURRENT_YEAR = new Date().getFullYear();
const yearOptions = Array.from(
  { length: Math.max(0, CURRENT_YEAR - BASE_YEAR + 1) },
  (_, i) => BASE_YEAR + i,
);

function safeParse(json, fallback = {}) {
  try {
    return JSON.parse(json || "") ?? fallback;
  } catch {
    return fallback;
  }
}

function getStoredUser() {
  const user = safeParse(localStorage.getItem("user"), {});
  const displayName =
    localStorage.getItem("display_name") ||
    user.display_name ||
    user.username ||
    localStorage.getItem("username") ||
    "User Acc";
  const userId = user.user_id ?? user.id ?? "";
  return { displayName, userId };
}

const toTitleCase = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/(^|[\s\-'])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase());

const phoneFmt =(cc, pn) => [cc || "", pn || ""].join(" ").trim() || "-";

const fmtSubmissionDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(+d)) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

const fmtInt = (n) => {
  const x = Number(n || 0);
  return Number.isFinite(x) ? x.toLocaleString() : "0";
};

function pickLearnDoSetting(settingsArray) {
  const arr = Array.isArray(settingsArray) ? settingsArray : [];
  if (!arr.length) return null;

  const normalize = (v) =>
    String(v || "")
      .toLowerCase()
      .replace(/\s+/g, "");

  const candidates = arr.filter((r) => {
    const a = normalize(r.module);
    const b = normalize(r.module_name);
    const c = normalize(r.name);
    const d = normalize(r.moduleCode);
    const e = normalize(r.code);

    return (
      a.includes("learndo") ||
      b.includes("learndo") ||
      c.includes("learndo") ||
      d.includes("learndo") ||
      e.includes("learndo") ||
      a.includes("learn+do") ||
      b.includes("learn+do") ||
      c.includes("learn+do") ||
      (a.includes("learn") && a.includes("do")) ||
      (b.includes("learn") && b.includes("do")) ||
      (c.includes("learn") && c.includes("do"))
    );
  });

  return candidates[0] || arr[0];
}

function normalizeStatus(status) {
  return String(status || "")
    .trim()
    .toLowerCase();
}

function getStatusColor(status) {
  const s = normalizeStatus(status);

  if (s === "approved") return "#1f9d55";
  if (s === "pending") return "#f4b400";
  if (s === "re-submit" || s === "resubmit") return "#FF692A";
  if (s === "rejected") return "#2d2d2d";

  return "#cbd5e1";
}

function formatStatusLabel(status) {
  const s = normalizeStatus(status);

  if (!s) return "-";
  if (s === "approved") return "Approved";
  if (s === "pending") return "Pending";
  if (s === "re-submit" || s === "resubmit") return "Resubmit";
  if (s === "rejected") return "Rejected";

  return String(status);
}

function parseStatuses(value) {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function uniqueStatusesKeepOrder(statuses) {
  const seen = new Set();
  const result = [];

  for (const s of statuses) {
    const key = normalizeStatus(s);
    if (!key) continue;
    if (!seen.has(key)) {
      seen.add(key);
      result.push(s);
    }
  }

  return result;
}

function isAllStatusesApprovedOrRejected(statuses) {
  const cleanStatuses = Array.isArray(statuses)
    ? statuses.map(normalizeStatus).filter(Boolean)
    : [];

  if (!cleanStatuses.length) return false;

  return cleanStatuses.every((s) => s === "approved" || s === "rejected");
}

function StatusBar({ statuses }) {
  const cleanStatuses = Array.isArray(statuses) ? statuses.filter(Boolean) : [];

  if (!cleanStatuses.length) {
    return (
      <div
        style={{
          width: 10,
          minWidth: 10,
          height: 42,
          borderRadius: 999,
          background: "#e9ecef",
          border: "1px solid rgba(0,0,0,0.08)",
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: 10,
        minWidth: 10,
        height: 42,
        overflow: "hidden",
        borderRadius: 999,
        border: "1px solid rgba(0,0,0,0.08)",
        display: "flex",
        flexDirection: "column",
        background: "#f8f9fa",
      }}
    >
      {cleanStatuses.map((status, idx) => (
        <div
          key={`${status}-${idx}`}
          style={{
            flex: 1,
            background: getStatusColor(status),
            minHeight: 8,
          }}
          title={formatStatusLabel(status)}
        />
      ))}
    </div>
  );
}

function StatusLegend() {
  const items = [
    { label: "Pending", color: "#f4b400" },
    { label: "Resubmit", color: "#FF692A" },
    { label: "Rejected", color: "#2d2d2d" },
    { label: "Approved", color: "#1f9d55" },
  ];

  return (
    <div className="d-flex flex-wrap gap-3 align-items-center">
      {items.map((item) => (
        <div key={item.label} className="d-flex align-items-center gap-2">
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: 999,
              background: item.color,
              display: "inline-block",
            }}
          />
          <span className="small text-muted">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

export default function LearnDo() {
  const { displayName, userId } = useMemo(getStoredUser, []);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [year, setYear] = useState(String(Math.max(CURRENT_YEAR, BASE_YEAR)));
  const [search, setSearch] = useState("");
  const [hideApprovedOnly, setHideApprovedOnly] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsErr, setSettingsErr] = useState("");
  const [settings, setSettings] = useState([]);
  const [learnDoSetting, setLearnDoSetting] = useState(null);
  const [updatingAI, setUpdatingAI] = useState(false);

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("auth_token") ||
    "";

  const authHeaders = useMemo(() => {
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, [token]);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    setSettingsErr("");
    try {
      if (!API_BASE) {
        setSettings([]);
        setLearnDoSetting(null);
        return;
      }

      const { data } = await axios.get(ENDPOINT_GET_SETTINGS, {
        headers: authHeaders,
        timeout: 20000,
      });

      if (data?.success && Array.isArray(data?.data)) {
        setSettings(data.data);
        setLearnDoSetting(pickLearnDoSetting(data.data));
      } else {
        setSettings([]);
        setLearnDoSetting(null);
        setSettingsErr(data?.message || "Failed to load settings.");
      }
    } catch (e) {
      setSettings([]);
      setLearnDoSetting(null);
      setSettingsErr("Server error while loading settings.");
    } finally {
      setSettingsLoading(false);
    }
  }, [authHeaders]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");

    try {
      if (!API_BASE) {
        setRows([]);
        return;
      }

      const { data } = await axios.get(ENDPOINT_VERIFIED, {
        params: { year },
        headers: authHeaders,
        timeout: 20000,
      });

      if (data?.success && Array.isArray(data?.data)) {
        setRows(
          data.data.map((r, idx) => {
            const rawStatuses = parseStatuses(r.activityStatuses);
            const visibleStatuses = uniqueStatusesKeepOrder(rawStatuses);

            return {
              id: r.user_id ?? idx + 1,
              user_id: r.user_id ?? idx + 1,
              username: r.username ?? "-",
              first_name: r.first_name ?? "",
              last_name: r.last_name ?? "",
              phone: phoneFmt(r.country_code, r.phone_number),
              badge_no: r.badge_no ?? "-",
              tg_expiry_date: r.tg_expiry_date ?? "-",
              status_name: r.status_name ?? r.user_status ?? "-",
              submissionDate: fmtSubmissionDate(r.created_date),
              hasNotify: Number(r.hasNotify || 0) === 1,
              ctrLearn: Number(r.ctrLearn || 0),
              ctrDo: Number(r.ctrDo || 0),
              activityStatuses: rawStatuses,
              displayStatuses: visibleStatuses,
              allApprovedRejectedOnly:
                isAllStatusesApprovedOrRejected(rawStatuses),
            };
          }),
        );
      } else {
        setRows([]);
        setErr(data?.message || "Failed to load data.");
      }
    } catch (e) {
      setRows([]);
      setErr("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [year, authHeaders]);

  const updateAIEnabled = useCallback(
    async (nextEnabled) => {
      if (!learnDoSetting?.id) {
        setSettingsErr("Cannot update: module setting row not found.");
        return;
      }

      setUpdatingAI(true);
      setSettingsErr("");

      try {
        const payload = {
          id: learnDoSetting.id,
          isEnabled: nextEnabled ? 1 : 0,
          isForAll:
            learnDoSetting.isForAll === 1 || learnDoSetting.isForAll === "1"
              ? 1
              : 0,
          user_id: userId || learnDoSetting.user_id || 0,
        };

        const { data } = await axios.post(ENDPOINT_UPDATE_SETTING, payload, {
          headers: authHeaders,
          timeout: 20000,
        });

        if (data?.success) {
          await loadSettings();
        } else {
          setSettingsErr(data?.message || "Failed to update setting.");
        }
      } catch (e) {
        setSettingsErr("Server error while updating setting.");
      } finally {
        setUpdatingAI(false);
      }
    },
    [learnDoSetting, userId, authHeaders, loadSettings],
  );

  useEffect(() => {
    setYear((prev) => {
      const n = Number(prev);
      return String(Number.isFinite(n) && n >= BASE_YEAR ? n : CURRENT_YEAR);
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    const totalPagesNow = Math.ceil(filteredRows.length / pageSize) || 1;
    if (page > totalPagesNow) {
      setPage(1);
    }
  }, [page, pageSize]);

  const clearFilters = () => {
    setYear(String(CURRENT_YEAR));
    setSearch("");
    setHideApprovedOnly(false);
    setPage(1);
  };

  const handleView = (row) => {
    const uid = row.user_id || row.id;
    navigate("/board", { state: { userId: String(uid), year: String(year) } });
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  const handleYearChange = (e) => {
    const v = Number(e.target.value);
    if (yearOptions.includes(v)) {
      setYear(String(v));
      setPage(1);
    }
  };

  const filteredRows = rows.filter((r) => {
    const name = `${r.first_name || ""} ${r.last_name || ""}`.toLowerCase();
    const needle = search.toLowerCase();

    const matchesSearch =
      !needle ||
      (r.username || "").toLowerCase().includes(needle) ||
      (r.badge_no || "").toLowerCase().includes(needle) ||
      name.includes(needle);

    if (!matchesSearch) return false;

    if (hideApprovedOnly && r.allApprovedRejectedOnly) {
      return false;
    }

    return true;
  });

  const totalPages = Math.ceil(filteredRows.length / pageSize) || 1;
  const paginatedRows = filteredRows.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

const totals = useMemo(() => {
  const activeTGRows = rows.filter(
    (r) => String(r.status_name || "").toLowerCase().trim() === "active"
  );

  const pendingRows = filteredRows.filter((r) => {
    const statuses = (r.displayStatuses || []).map((s) =>
      String(s).toLowerCase().trim()
    );

    if (!statuses.length) return false;

    return statuses.every(
      (status) =>
        status === "pending" ||
        status === "resubmit" ||
        status === "re-submit"
    );
  });

  const sumLearn = pendingRows.reduce(
    (acc, r) => acc + (Number(r.ctrLearn) || 0),
    0
  );

  const sumDo = pendingRows.reduce(
    (acc, r) => acc + (Number(r.ctrDo) || 0),
    0
  );

  return {
    totalUsers: activeTGRows.length,
    sumLearn,
    sumDo,
    sumAll: sumLearn + sumDo,
  };
}, [rows, filteredRows]);

  const aiEnabled =
    learnDoSetting?.isEnabled === 1 || learnDoSetting?.isEnabled === "1";

  const StatCard = ({ title, value, icon, accent }) => (
    <Card
      className="h-100"
      style={{
        borderRadius: 16,
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 8px 22px rgba(0,0,0,0.06)",
      }}
    >
      <Card.Body className="p-3">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div>
            <div className="text-muted small" style={{ letterSpacing: 0.2 }}>
              {title}
            </div>
            <div className="fw-bold" style={{ fontSize: 28, lineHeight: 1.15 }}>
              {fmtInt(value)}
            </div>
          </div>

          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.04)",
              border: `1px solid ${accent}33`,
            }}
          >
            <span style={{ color: accent }}>{icon}</span>
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <div
      className="custom-font"
      style={{ minHeight: "100vh", background: "#f7f9fc" }}
    >
      <Container>
        <div
          className="d-flex align-items-center justify-content-between py-2"
          style={{ gap: 16 }}
        >
          <img src={LOGO_URL} alt="TMIS" style={{ height: 50 }} />
          <div className="d-flex align-items-center" style={{ gap: 25 }}>
            <Bell size={20} color="#1A3584" />
            <SettingsGear />
            <BoxArrowRight
              size={20}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={handleLogout}
              role="button"
              aria-label="Logout"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleLogout();
              }}
            />
            <span>{displayName}</span>
            <PersonCircle size={23} color="#1A3584" />
          </div>
        </div>
      </Container>

      <div
        style={{
          height: 64,
          background: NAV_BLUE,
          color: "white",
          display: "flex",
          alignItems: "center",
          marginTop: 8,
        }}
      >
        <Container>
          <div
            className="container-xxl d-flex align-items-center justify-content-between"
            style={{ gap: 12 }}
          >
            <div className="d-flex align-items-center" style={{ gap: 12 }}>
              <span
                role="button"
                aria-label="Go back"
                tabIndex={0}
                onClick={handleBack}
                onKeyDown={(e) =>
                  e.key === "Enter" || e.key === " " ? handleBack() : null
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  background: "transparent",
                  cursor: "pointer",
                  outline: "none",
                }}
                className="back-icon"
              >
                <ArrowLeft size={18} color="#ffffff" />
              </span>
              <h4 className="m-0 fw-bold">LEARN + DO ACTIVITIES</h4>
            </div>

            <div
              className="d-flex align-items-center gap-2"
              style={{
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.20)",
                borderRadius: 999,
                padding: "6px 12px",
              }}
            >
              <Robot size={18} />
              <span className="small fw-semibold">Enable AI</span>

              {settingsLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <Form.Check
                  type="switch"
                  id="toggle-ai"
                  checked={!!aiEnabled}
                  disabled={updatingAI || !learnDoSetting?.id}
                  onChange={(e) => updateAIEnabled(e.target.checked)}
                  style={{ marginLeft: 6 }}
                />
              )}
            </div>
          </div>
        </Container>
      </div>

      <Container style={{ paddingTop: 22, paddingBottom: 40 }}>
        {!!settingsErr && (
          <Alert
            variant="warning"
            className="mb-3"
            style={{ borderRadius: 14 }}
          >
            {settingsErr}
          </Alert>
        )}

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <InputGroup style={{ maxWidth: 320 }}>
            <InputGroup.Text>
              <Search size={16} />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search username, badge or name..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </InputGroup>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <InputGroup style={{ width: 200 }}>
              <InputGroup.Text>
                <CalendarEvent size={16} />
              </InputGroup.Text>
              <Form.Select value={year} onChange={handleYearChange}>
                {yearOptions.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>

            <Button
              variant="primary"
              style={{ background: BRAND, borderColor: BRAND }}
              onClick={load}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" /> Loading
                </>
              ) : (
                "Apply"
              )}
            </Button>

            <Button
              variant="outline-secondary"
              onClick={clearFilters}
              disabled={loading}
            >
              Clear
            </Button>

            <div className="d-flex align-items-center">
              <Form.Check
                type="checkbox"
                id="hide-approved-only"
                className="mb-0"
                label="Hide Approved / Rejected"
                checked={hideApprovedOnly}
                onChange={(e) => {
                  setHideApprovedOnly(e.target.checked);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </div>

        <Row className="g-4">
          <Col xs={12}>
            <Card
              style={{
                background: PANEL_BG,
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
                borderRadius: 18,
              }}
            >
              <Card.Body style={{ background: "white", borderRadius: 16 }}>
                <Row className="g-3 mb-3">
                  <Col xs={12} md={6} xl={3}>
                    <StatCard
                      title="Total Tourist Guides"
                      value={totals.totalUsers}
                      icon={<People size={22} />}
                      accent={NAV_BLUE}
                    />
                  </Col>
                  <Col xs={12} md={6} xl={3}>
                    <StatCard
                      title="Requires Action in LEARN"
                      value={totals.sumLearn}
                      icon={<Book size={22} />}
                      accent={BRAND}
                    />
                  </Col>
                  <Col xs={12} md={6} xl={3}>
                    <StatCard
                      title="Requires Action in DO"
                      value={totals.sumDo}
                      icon={<Check2Circle size={22} />}
                      accent="#2e9b67"
                    />
                  </Col>
                  <Col xs={12} md={6} xl={3}>
                    <StatCard
                      title="Total"
                      value={totals.sumAll}
                      icon={<Layers size={22} />}
                      accent="#ff8a00"
                    />
                  </Col>
                </Row>

                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <div>
                    <div className="fw-semibold" style={{ color: NAV_BLUE }}>
                      Activity Status
                    </div>
                    <div className="text-muted small">
                      Color indicators represent the latest status recorded for
                      each activity.
                    </div>
                  </div>

                  <StatusLegend />
                </div>

                <div className="table-responsive">
                  <Table hover align="middle" className="mb-0">
                    <thead
                      style={{
                        background: PANEL_SURFACE,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <tr>
                        <th>Username</th>
                        <th>Name</th>
                        <th>Badge No</th>
                        <th>TG Expiry Date</th>
                        <th className="text-center">Status</th>
                        <th className="text-center">LEARN</th>
                        <th className="text-center">DO</th>
                        <th className="text-center">Total</th>
                        <th>Submission Date</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={10} className="text-center py-4">
                            <Spinner size="sm" className="me-2" /> Loading…
                          </td>
                        </tr>
                      ) : err ? (
                        <tr>
                          <td
                            colSpan={10}
                            className="text-center py-4 text-danger"
                          >
                            {err}
                          </td>
                        </tr>
                      ) : paginatedRows.length ? (
                        paginatedRows.map((r) => {
                          const name =
                            toTitleCase(
                              `${r.first_name || ""} ${r.last_name || ""}`.trim(),
                            ) || "-";
                          const total = (r.ctrLearn || 0) + (r.ctrDo || 0);

                          return (
                            <tr key={r.id}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  {r.hasNotify && (
                                    <span
                                      title="Notify"
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: 20,
                                        height: 20,
                                        minWidth: 20,
                                        borderRadius: "50%",
                                        background: "#dc2626",
                                        color: "#fff",
                                        fontSize: 11,
                                        fontWeight: 800,
                                        lineHeight: 1,
                                      }}
                                    >
                                      R
                                    </span>
                                  )}
                                  <span>{r.username}</span>
                                </div>
                              </td>
                              <td>{name}</td>
                              <td>{r.badge_no}</td>
                              <td>{r.tg_expiry_date}</td>

                              <td
                                style={{ minWidth: 20 }}
                                className="text-center align-middle"
                              >
                                <div className="d-flex justify-content-center align-items-center">
                                  <StatusBar statuses={r.displayStatuses} />
                                </div>
                              </td>

                              <td className="text-center">{r.ctrLearn}</td>
                              <td className="text-center">{r.ctrDo}</td>
                              <td className="text-center">{total}</td>
                              <td>{r.submissionDate}</td>
                              <td className="text-center">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => handleView(r)}
                                >
                                  View
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={10} className="text-center py-4">
                            <div className="text-muted">
                              No verified users found for {year}.
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">Rows per page:</span>
                    <Form.Select
                      size="sm"
                      style={{ width: 70 }}
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                    >
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={120}>120</option>
                      <option value={150}>150</option>
                    </Form.Select>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </Button>
                    <span className="small">
                      Page {page} / {totalPages}
                    </span>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page >= totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
